import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  Divider,
  LinearProgress,
  Link as BaseLink,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import { Link, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import {
  AUTH_QUERY,
  DELETE_SMART_IMPORT_MUTATION,
  SMART_IMPORT_QUERY,
} from '../../gql';
import { Main } from '../../components';
import {
  formatImportDuration,
  ImportStatusChip,
  isImportActive,
  isImportCancellable,
} from './importStatus';
import { SmartImportRecord } from './types';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatImportFailure } from './importMessages';

const detailPaperStyles = { p: { xs: 2, md: 3 }, mb: 3 };
const detailsGridStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
  columnGap: 2,
  rowGap: 1,
};
const labelStyles = { color: 'text.secondary', fontWeight: 500 };
const highlightedLabelStyles = { fontWeight: 600 };
const progressStyles = { minWidth: 220, flexGrow: 1 };
const wrapTextStyles = { overflowWrap: 'anywhere' };
const detailHeaderDirection = { xs: 'column', sm: 'row' } as const;
const errorAlertStyles = { mt: 3 };

interface DetailValueProps {
  label: string;
  highlighted?: boolean;
  children: React.ReactNode;
}

const DetailValue = ({
  label,
  highlighted = false,
  children,
}: DetailValueProps) => (
  <>
    <Typography sx={highlighted ? highlightedLabelStyles : labelStyles}>
      {label}
    </Typography>
    <Typography component="div" sx={wrapTextStyles}>
      {children}
    </Typography>
  </>
);

function formatDate(value: string | null): string {
  return value ? DateTime.fromISO(value).toFormat('dd.LL.yyyy HH:mm:ss') : '—';
}

export const ImportDetail = React.memo(() => {
  const intl = useIntl();
  const { importId } = useParams();
  const [now, setNow] = useState(Date.now());
  const [cancelError, setCancelError] = useState<string | null>(null);
  const { data, error, loading, refetch, startPolling, stopPolling } = useQuery(
    SMART_IMPORT_QUERY,
    {
      variables: { id: importId },
      fetchPolicy: 'cache-and-network',
      skip: !importId,
    }
  );
  const { data: authData } = useQuery(AUTH_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const [cancelImport, { loading: cancelling }] = useMutation(
    DELETE_SMART_IMPORT_MUTATION
  );
  const smartImport = data?.smartImport as SmartImportRecord | null | undefined;
  const smartImportStatus = smartImport?.status;

  useEffect(() => {
    if (smartImportStatus && isImportActive(smartImportStatus)) {
      startPolling(3000);
    } else {
      stopPolling();
    }

    return stopPolling;
  }, [smartImportStatus, startPolling, stopPolling]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, []);

  const handleCancel = useCallback(async () => {
    if (!importId) {
      return;
    }

    setCancelError(null);

    try {
      await cancelImport({ variables: { id: importId } });
      await refetch();
    } catch {
      setCancelError(
        intl.formatMessage({
          id: 'imports.error.stop',
          defaultMessage: 'The import could not be stopped.',
        })
      );
    }
  }, [cancelImport, importId, intl, refetch]);

  const creator = useMemo(() => {
    const user = authData?.viewer?.user;

    if (user?.id !== smartImport?.createdBy) {
      return smartImport?.createdBy || '—';
    }

    return user.displayName || user.email || user.id;
  }, [authData?.viewer?.user, smartImport?.createdBy]);

  if (loading && !smartImport) {
    return (
      <Main>
        <LinearProgress />
      </Main>
    );
  }

  if (error || !smartImport) {
    return (
      <Main>
        <Alert severity="error">
          <FormattedMessage
            id="imports.detail.error.load"
            defaultMessage="Import could not be loaded."
          />
        </Alert>
      </Main>
    );
  }

  const progressValue = smartImport.progress.totalRowCount
    ? (smartImport.progress.processedRowCount /
        smartImport.progress.totalRowCount) *
      100
    : 0;
  const sourceAvailable =
    !smartImport.sourceDeletedAt &&
    DateTime.fromISO(smartImport.expiresAt).toMillis() > now;
  const duration = formatImportDuration(
    smartImport.startedAt,
    smartImport.completedAt,
    smartImport.updatedAt,
    smartImport.status,
    now,
    intl.formatMessage({
      id: 'imports.notStarted',
      defaultMessage: 'Not started',
    })
  );

  return (
    <Main>
      <Paper sx={detailPaperStyles}>
        <Stack
          direction={detailHeaderDirection}
          justifyContent="space-between"
          gap={2}
          mb={3}
        >
          <Stack gap={1}>
            <Typography variant="h5" fontWeight={500}>
              <FormattedMessage
                id="imports.detail.heading"
                defaultMessage="Import {id}"
                values={{ id: smartImport.id }}
              />
            </Typography>
            <Stack gap={1} direction="row" alignItems="flex-start">
              <Typography sx={highlightedLabelStyles}>
                <FormattedMessage
                  id="imports.detail.status"
                  defaultMessage="Status"
                />
              </Typography>
              <ImportStatusChip status={smartImport.status} />
            </Stack>
          </Stack>
          <Stack direction="row" gap={1} alignItems="flex-start">
            {(isImportActive(smartImport.status) ||
              smartImport.status === 'failed') && (
              <Button
                component={Link}
                startIcon={<PlayArrowOutlinedIcon />}
                to={`/imports/${smartImport.id}/workflow`}
                variant="contained"
              >
                <FormattedMessage
                  id="imports.continue"
                  defaultMessage="Continue import"
                />
              </Button>
            )}
            {isImportCancellable(smartImport.status) && (
              <Button
                color="error"
                disabled={cancelling}
                onClick={handleCancel}
                startIcon={<StopCircleOutlinedIcon />}
                variant="outlined"
              >
                <FormattedMessage id="imports.stop" defaultMessage="Stop" />
              </Button>
            )}
          </Stack>
        </Stack>

        <Box sx={detailsGridStyles}>
          <DetailValue
            label={intl.formatMessage({
              id: 'imports.detail.startedBy',
              defaultMessage: 'Started by',
            })}
          >
            {creator}
          </DetailValue>
          <DetailValue
            label={intl.formatMessage({
              id: 'general.created',
              defaultMessage: 'Created',
            })}
          >
            {formatDate(smartImport.createdAt)}
          </DetailValue>
          <DetailValue
            label={intl.formatMessage({
              id: 'imports.detail.processingStarted',
              defaultMessage: 'Processing started',
            })}
          >
            {formatDate(smartImport.startedAt)}
          </DetailValue>
          <DetailValue
            label={intl.formatMessage({
              id: 'imports.status.completed',
              defaultMessage: 'Completed',
            })}
          >
            {formatDate(smartImport.completedAt)}
          </DetailValue>
          <DetailValue
            label={intl.formatMessage({
              id: 'imports.runningTime',
              defaultMessage: 'Running time',
            })}
          >
            {duration}
          </DetailValue>
          <DetailValue
            label={intl.formatMessage({
              id: 'imports.rows',
              defaultMessage: 'Rows',
            })}
          >
            {smartImport.rowCount}
          </DetailValue>
          <DetailValue
            label={intl.formatMessage({
              id: 'imports.detail.providerModel',
              defaultMessage: 'Provider model',
            })}
          >
            {smartImport.providerModel || '—'}
          </DetailValue>
          <DetailValue
            label={intl.formatMessage({
              id: 'imports.detail.tokenUsage',
              defaultMessage: 'Token usage',
            })}
          >
            <FormattedMessage
              id="imports.detail.tokenCounts"
              defaultMessage="{input} input / {output} output"
              values={{
                input: smartImport.inputTokens,
                output: smartImport.outputTokens,
              }}
            />
          </DetailValue>
        </Box>

        {isImportActive(smartImport.status) && (
          <Box sx={detailsGridStyles} mt={3}>
            <Typography sx={labelStyles}>
              <FormattedMessage
                id="imports.detail.processed"
                defaultMessage="Processed"
              />
            </Typography>
            <Stack direction="row" alignItems="center" gap={2}>
              <LinearProgress
                sx={progressStyles}
                variant="determinate"
                value={progressValue}
              />
              <Typography variant="body2">
                {smartImport.progress.processedRowCount} /{' '}
                {smartImport.progress.totalRowCount}
              </Typography>
            </Stack>
          </Box>
        )}

        {(cancelError || smartImport.error) && (
          <Alert severity="error" sx={errorAlertStyles}>
            {cancelError || formatImportFailure(smartImport.error, intl)}
          </Alert>
        )}
      </Paper>

      <Paper sx={detailPaperStyles}>
        <Typography variant="h6" mb={2}>
          <FormattedMessage
            id="imports.detail.artifacts"
            defaultMessage="Artifacts"
          />
        </Typography>
        <Stack gap={2}>
          <Box>
            <Typography fontWeight={500}>
              {smartImport.originalFilename}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {sourceAvailable ? (
                <FormattedMessage
                  id="imports.detail.sourceRetained"
                  defaultMessage="Temporary source retained until {date}"
                  values={{ date: formatDate(smartImport.expiresAt) }}
                />
              ) : (
                <FormattedMessage
                  id="imports.detail.sourceDeleted"
                  defaultMessage="Temporary source deleted"
                />
              )}
            </Typography>
          </Box>
          <Divider />
          <Box>
            <Typography fontWeight={500}>
              <FormattedMessage
                id="imports.detail.parsedData"
                defaultMessage="Parsed data"
              />
            </Typography>
            <Typography color="text.secondary" variant="body2">
              <FormattedMessage
                id="imports.detail.parsedDataSummary"
                defaultMessage="{count, plural, one {# row} other {# rows}}, {format}, {encoding}"
                values={{
                  count: smartImport.analysis.rowCount,
                  format: smartImport.sourceFormat,
                  encoding: smartImport.analysis.encoding,
                }}
              />
            </Typography>
          </Box>
          {smartImport.draft && (
            <>
              <Divider />
              <Box>
                <Typography fontWeight={500}>
                  <FormattedMessage
                    id="imports.detail.reviewDraft"
                    defaultMessage="Review draft"
                  />
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  <FormattedMessage
                    id="imports.detail.draftSummary"
                    defaultMessage="Revision {revision}, {count, plural, one {# row} other {# rows}}"
                    values={{
                      revision: smartImport.draft.revision,
                      count: smartImport.draft.rows.length,
                    }}
                  />
                </Typography>
              </Box>
            </>
          )}
          <Divider />
          <Box>
            <Typography fontWeight={500} mb={1}>
              <FormattedMessage
                id="imports.detail.createdFinds"
                defaultMessage="Created Finds ({count})"
                values={{ count: smartImport.committedFindIds.length }}
              />
            </Typography>
            {smartImport.committedFindIds.length === 0 ? (
              <Typography color="text.secondary">
                <FormattedMessage
                  id="imports.detail.noFinds"
                  defaultMessage="No Finds stored yet."
                />
              </Typography>
            ) : (
              <Stack gap={0.5}>
                {smartImport.committedFindIds.map((findId) => (
                  <BaseLink
                    component={Link}
                    key={findId}
                    to={`/f/${findId}/edit`}
                  >
                    {findId}
                  </BaseLink>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>
    </Main>
  );
});
